import {
  useInfiniteQuery,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import {hasOwnProperty} from 'quickstart/utils'
import {admin, api, ApiError, Falsy, logger} from 'tizra'
import {AsyncClient} from 'tizra/client'
import {AsyncReturnType, JsonValue} from 'type-fest'

const log = logger('useApi')

/**
 * React hooks for calling the Tizra API with useQuery.
 */
export const useApi = makeUseApi(api!, false)
export const useInfiniteApi = makeUseApi(api!, true)
export const useAdminApi = makeUseApi(admin!, false)
export const useInfiniteAdminApi = makeUseApi(admin!, true)
export const useApis = makeUseApis(api!)
export const useAdminApis = makeUseApis(admin!)

type Params<K extends keyof AsyncClient> = Parameters<AsyncClient[K]>[0]

type UseApiOptions<K extends keyof AsyncClient> = Omit<
  UseQueryOptions<AsyncReturnType<AsyncClient[K]>, ApiError>,
  'queryKey' | 'queryFn' | 'enabled'
>

type UseApiReturnType<K extends keyof AsyncClient> = UseQueryResult<
  AsyncReturnType<AsyncClient[K]>,
  ApiError
>

export type UseApi = {
  [K in keyof AsyncClient]: Params<K> extends string
    ? (
        firstArg: string | [string] | [string, JsonValue] | Falsy,
        options?: UseApiOptions<K>,
      ) => UseApiReturnType<K>
    : Params<K> extends object // i.e. required
      ? (
          params: Params<K> | Falsy,
          options?: UseApiOptions<K>,
        ) => UseApiReturnType<K>
      : (
          params?: Params<K> | Falsy | true,
          options?: UseApiOptions<K>,
        ) => UseApiReturnType<K>
}

/**
 * Make a useApi hook for accessing the Tizra API. This is not exported, because
 * it's only used to define the hooks statically above: useApi, useInfiniteApi,
 * useAdminApi, useInfiniteAdminApi.
 */
function makeUseApi(api: AsyncClient, infinite: false): UseApi
function makeUseApi(
  api: AsyncClient,
  infinite: true,
): Record<string, (...queryArgs: any) => ReturnType<typeof useInfiniteQuery>>
function makeUseApi(api: any, infinite: boolean) {
  return Object.fromEntries(
    Object.keys(api).map(name => [
      name,
      (...queryArgs: any[]) => {
        const queryParams = makeQueryParams({name, api, infinite, queryArgs})
        const queryResult = infinite
          ? useInfiniteQuery(queryParams)
          : useQuery(queryParams)
        if (queryResult?.error) {
          log.error(queryResult.error)
        }
        return queryResult
      },
    ]),
  )
}

/**
 * Make a useApis hook for accessing the Tizra API. This is not exported, because
 * it's only used to define the hooks statically above: useApis and useAdminApis.
 */
function makeUseApis(api: any) {
  return (queriesArgs: Array<[string, ...any]>) => {
    const queries = queriesArgs.map(([name, ...queryArgs]) =>
      makeQueryParams({name, api, infinite: false, queryArgs}),
    )
    const queriesResult = useQueries({queries})
    queriesResult.forEach(queryResult => {
      if (queryResult?.error) {
        log.error(queryResult.error)
      }
    })
    return queriesResult
  }
}

function makeQueryParams({
  name,
  api,
  infinite,
  queryArgs,
}: {
  name: string
  api: any
  infinite: boolean
  queryArgs: any[]
}) {
  const [firstArg, {volatile = false, ...queryConfig} = {}] = queryArgs
  const call = api[name]

  // queryKey is only falsy if it was explicitly passed, not if it was
  // omitted. This is why we have to collect queryArgs before
  // destructuring, so that we can check the length.
  const queryKey: any[] | undefined =
    queryArgs.length && !firstArg // undefined, null, false, '', 0
      ? undefined
      : [name].concat(
          // At this point, undefined definitively means firstArg was
          // omitted, because of queryArgs.length check above.
          [true, undefined].includes(firstArg)
            ? []
            : Array.isArray(firstArg) // useApi.GET(['foo', {params}])
              ? firstArg
              : [firstArg],
        )

  // For useInfiniteQuery, start will be appended to the args, for example
  // useInfiniteApi.search(params, start) should integrate start into params.
  const queryFn = !queryKey
    ? undefined
    : infinite
      ? ({pageParam: start}: {pageParam?: number}) =>
          typeof queryKey[1] === 'object' && start
            ? call({...queryKey[1], start}, ...queryKey.slice(2))
            : call(...queryKey.slice(1))
      : () => call(...queryKey.slice(1))

  const config = {
    // react-query v2 changed from disabling by falsy key.
    // https://github.com/TanStack/query/releases/tag/v2.0.0
    enabled: !!queryKey,

    // react-query will retry failed API calls by default. This behavior
    // only really makes sense in large systems where the API server farm
    // might have troubled nodes or some kind of non-deterministic fast
    // failures. For the Tizra server, a failure is unlikely to become
    // a success just by calling back.
    retry: false,

    // Tizra data isn't typically in flux. Plan to cache everything and
    // avoid refetching, unless told otherwise.
    ...(volatile || {
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }),

    ...api.queryConfig,
    ...call.queryConfig,
    ...queryConfig,
  }

  return {queryKey, queryFn, ...config, getNextPageParam}
}

function getNextPageParam(resp: unknown): number | undefined {
  if (
    resp &&
    typeof resp === 'object' &&
    hasOwnProperty(resp, 'next') &&
    typeof resp.next === 'string' &&
    hasOwnProperty(resp, 'size') &&
    typeof resp.size === 'number'
  ) {
    const queryString = resp.next.split('?', 2)[1] || ''
    const nextStart = parseInt(
      new URLSearchParams(queryString).get('start') || '',
      10,
    )
    if (typeof nextStart === 'number' && nextStart < resp.size) {
      return nextStart
    }
  }
}
